import { MoreVertical, Trash2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { NostrEvent } from '@nostrify/nostrify';
import { useToast } from '@/hooks/useToast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { useState } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';

interface DeleteListButtonProps {
  listEvent: NostrEvent;
  onDeleted?: () => void;
}

export const DeleteListButton = ({ listEvent, onDeleted }: DeleteListButtonProps) => {
  const { mutate: publishEvent, isPending } = useNostrPublish();
  const { toast } = useToast();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const { user } = useCurrentUser();

  const handleDelete = () => {
    publishEvent(
      {
        kind: 5,
        tags: [['e', listEvent.id]],
        content: 'Deleted deck list',
      },
      {
        onSuccess: () => {
          toast({ title: "List deleted successfully!" });
          if (onDeleted) {
            onDeleted();
          }
        },
        onError: () => {
          toast({ title: "Failed to delete list", variant: "destructive" });
        },
        onSettled: () => {
          setIsAlertOpen(false);
        }
      }
    );
  };

  if (user?.pubkey !== listEvent.pubkey) {
    return null;
  }

  return (
    <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <AlertDialogTrigger asChild>
            <DropdownMenuItem className="text-destructive">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete List
            </DropdownMenuItem>
          </AlertDialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure you want to delete this list?</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. This will permanently delete the list from all relays.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} disabled={isPending}>
            {isPending ? 'Deleting...' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
